import PropTypes from "prop-types"
import { Row, Col, Collapse, Flex, Tooltip } from "antd"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import { numberToCurrency } from "helpers/string"
import { useTranslation } from "react-i18next"

const SummaryKeyValue = ({ details, valueAlign }) => {
  const { t } = useTranslation()
  if (!Array.isArray(details) || details.length === 0) return null
  const isAlignRight = valueAlign === "right"
  return details
    .filter(({ value }) => value !== undefined && value !== null && value !== "")
    .map(({ label, labelKey, count, value, fw, info, isDiscount, isSubtotalAdjusted, originalSubtotal, adjustedPrice, adjustmentReason }, idx) => {
      const labelText = labelKey ? t(labelKey, { count }) : label
      const textType = isDiscount ? "danger" : undefined

      if (isSubtotalAdjusted) {
        return (
          <div key={(labelKey || label || "subtotal") + idx}>
            <Row gutter={[0, 0]}>
            <Col xs={14} md={12}>
              <Flex align="center" gap={4}>
                <Text fw={fw || 600}>{labelText}</Text>
                {adjustmentReason && (
                  <Tooltip title={adjustmentReason}>
                    <Icon type="feather" name="Info" width={14} height={14} />
                  </Tooltip>
                )}
              </Flex>
            </Col>
            <Col xs={10} md={12} style={{ textAlign: isAlignRight ? "right" : "left" }}>
              {isAlignRight ? "" : "  "}
              {originalSubtotal != null && (
                <Text type="secondary" style={{ textDecoration: "line-through", marginRight: 6 }}>
                  {numberToCurrency(originalSubtotal)}
                </Text>
              )}
              <Text fw={fw || 600}>{numberToCurrency(value)}</Text>
              <Text type="secondary" style={{ marginLeft: 4 }}>
                ({t("client.payment.labels.adjusted", { defaultValue: "adjusted" })})
              </Text>
            </Col>
            </Row>
            {adjustedPrice != null && (
              <Row gutter={[0, 0]}>
                <Col xs={24} style={{ textAlign: isAlignRight ? "right" : "left" }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("client.payment.labels.adjusted_price", { defaultValue: "Adjusted price" })}: {numberToCurrency(adjustedPrice)}
                  </Text>
                </Col>
              </Row>
            )}
          </div>
        )
      }

      if (info?.length > 0) {
        return (
          <Collapse
            key={(labelKey || label || "item") + idx}
            ghost
            items={[
              {
                key: "tax-fees",
                styles: {
                  header: { padding: 0 },
                  body: { padding: "4px 0 0 16px" }
                },
                label: (
                  <Row gutter={[0, 0]}>
                    <Col xs={14} md={12}>
                      <Text fw={fw || 400}>{labelText}</Text>
                    </Col>
                    <Col xs={10} md={12} style={{ textAlign: isAlignRight ? "right" : "left" }}>
                      <Text fw={fw || 500}>
                        {isAlignRight ? "" : "\u00A0\u00A0"}
                        {numberToCurrency(value)}
                      </Text>
                    </Col>
                  </Row>
                ),
                children: info.map((child, childIdx) => (
                  <Row key={child.label + childIdx} gutter={[0, 0]} style={{ marginTop: 4 }}>
                    <Col xs={14} md={12}>
                      <Text type="secondary">{child.label}</Text>
                    </Col>
                    <Col xs={10} md={12} style={{ textAlign: isAlignRight ? "right" : "left" }}>
                      <Text type="secondary">
                        {isAlignRight ? "" : "\u00A0\u00A0"}
                        {child.amount}
                      </Text>
                    </Col>
                  </Row>
                ))
              }
            ]}
          />
        )
      }

      return (
        <Row key={(labelKey || label || "item") + idx} gutter={[0, 0]}>
          <Col xs={14} md={12}>
            <Text fw={fw || 400} type={textType}>{labelText}</Text>
          </Col>
          <Col xs={10} md={12} style={{ textAlign: isAlignRight ? "right" : "left" }}>
            <Text fw={fw || 500} type={textType}>
              {isAlignRight ? "" : "\u00A0\u00A0"}
              {numberToCurrency(value)}
            </Text>
          </Col>
        </Row>
      )
    })
}

SummaryKeyValue.propTypes = {
  details: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      labelKey: PropTypes.string,
      count: PropTypes.number,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      fw: PropTypes.number,
      isDiscount: PropTypes.bool,
      isSubtotalAdjusted: PropTypes.bool,
      originalSubtotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      adjustedPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      adjustmentReason: PropTypes.string
    })
  ),
  valueAlign: PropTypes.oneOf(["left", "right"])
}

export default SummaryKeyValue
